import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { initialGrid, puzzleNumber } from './puzzles/today';
import { initialGridMini, puzzleNumberMini } from './puzzles/todayMini';
import { warmupGrid, warmupFullGrid } from './puzzles/warmup';
import CanvasOverlay from "./components/CanvasOverlay";
import NumberOverlay from "./components/NumberOverlay";
import InteractiveTutorial from './components/InstructionExamples';
import {
  isValidCell,
  getAdjacent,
  isConnectedGroup,
  findConnectedGroupsThatSum,
  formatTime,
  getMaxSelection,
  getNextExpectedNumber,
  getPrefilledCluesSkippedBeforeNext,
  isNextNumberBlockedByClue,
  getHintMove
} from './utils/gameHelpers';

import StartScreen from './components/StartScreen';
import GameHeader from './components/GameHeader';
import WinScreen from './components/WinScreen';
import WarmupDoneScreen from './components/WarmupDoneScreen';
import FireworksCelebration, {
  prefersReducedMotion,
  WIN_CELEBRATION_MS,
  WIN_CELEBRATION_REDUCED_MS
} from './components/FireworksCelebration';
import GameControls from './components/GameControls';
import PrivacyPolicyModal from './PrivacyPolicy';
import AboutModal from './About';
import StrategyModal from './StrategyModal';
import Statistics from './components/Statistics';
import HighScores from './components/HighScores';
import { getDailyStreakInfo, recordDailyPuzzleWin, clearDailyStreak } from './utils/dailyStreak';

function markTutorialSeen() {
  try {
    localStorage.setItem("sums-tutorial-seen", "1");
  } catch (e) {
    // Storage can be blocked; closing the tutorial still has to work.
  }
}

function hasSeenTutorial() {
  try {
    return localStorage.getItem("sums-tutorial-seen") !== null;
  } catch (e) {
    return true;
  }
}

function readWarmupAvailable() {
  try {
    if (localStorage.getItem("sums-warmup-done") !== null) return false;
    if (localStorage.getItem("sums-game-history") !== null) return false;
    return true;
  } catch (e) {
    return false;
  }
}

function isWarmupMode(mode) {
  return mode === "warmup" || mode === "warmupFull";
}

export default function SumGridGame() {
  const HINT_COOLDOWN_MS = 5000;
  const [showStartScreen, setShowStartScreen] = useState(true);
  const [gameMode, setGameMode] = useState(null); // 'mini', 'full', 'warmup', or 'warmupFull'
  
  const puzzle = gameMode === 'warmup'
    ? warmupGrid
    : gameMode === 'warmupFull'
      ? warmupFullGrid
      : gameMode === 'mini'
        ? initialGridMini
        : initialGrid;
  const currentPuzzleNumber = isWarmupMode(gameMode) ? 'warm-up' : gameMode === 'mini' ? puzzleNumberMini : puzzleNumber;
  const [grid, setGrid] = useState(initialGrid);
  const [selectedCells, setSelectedCells] = useState([]);
  const [history, setHistory] = useState([initialGrid]);
  const [gameWon, setGameWon] = useState(false);
  const [showWinScreen, setShowWinScreen] = useState(false);
  const [showWarmupDoneScreen, setShowWarmupDoneScreen] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [tutorialEnding, setTutorialEnding] = useState('daily');
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [bestTimeMini, setBestTimeMini] = useState(null);
  const [bestTimeFull, setBestTimeFull] = useState(null);
  const [gameHistory, setGameHistory] = useState([]);
  const [celebratingCells, setCelebratingCells] = useState([]);
  const [showCelebration, setShowCelebration] = useState(false);
  const [placementPath, setPlacementPath] = useState([]);
  const timerRef = useRef(null);
  const [poppingCells, setPoppingCells] = useState([]);
  const [moveCount, setMoveCount] = useState(0);
  const GRID_SIZE = grid?.[0]?.length || 0;
  const [countdownToMidnight, setCountdownToMidnight] = useState("");
  const [hardMode, setHardMode] = useState(false);
  const [showHelpDropdown, setShowHelpDropdown] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [winScreenDismissed, setWinScreenDismissed] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showStrategyModal, setShowStrategyModal] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showHighScores, setShowHighScores] = useState(false);
  const [highScoresHighlight, setHighScoresHighlight] = useState(null);
  const [hintInProgress, setHintInProgress] = useState(false);
  const [hintCooldownByMode, setHintCooldownByMode] = useState({ mini: 0, full: 0, warmup: 0, warmupFull: 0 });
  const [lastSequence, setLastSequence] = useState(null);
  const [lastPlacedPosition, setLastPlacedPosition] = useState(null);
  const [dailyStreakInfo, setDailyStreakInfo] = useState(() => getDailyStreakInfo());
  const [lastWinStreakCount, setLastWinStreakCount] = useState(0);
  const cellSize = Math.min(80, Math.floor(window.innerWidth / (GRID_SIZE + 2)));

const todayStr = new Date().toLocaleDateString("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric"
});

const prefilledCells = useRef(new Set());
const modeStateRef = useRef({ mini: null, full: null, warmup: null, warmupFull: null });
const warmupFinishedRef = useRef(false);
const [overlayPoints, setOverlayPoints] = useState([]);
const gridWrapperRef = useRef(null);
const clueSkipAnimRef = useRef(null);
const celebrationTimersRef = useRef([]);
const winModalTimerRef = useRef(null);
const messageTimerRef = useRef(null);
const rejectedReasonsRef = useRef(new Set());
const [clueSkipFlashKeys, setClueSkipFlashKeys] = useState([]);
const [headerNextOverride, setHeaderNextOverride] = useState(null);
const clueFlashCells = useMemo(
  () => clueSkipFlashKeys.map((k) => {
    const [row, col] = k.split(",").map(Number);
    return { row, col };
  }),
  [clueSkipFlashKeys]
);

const clearClueSkipAnimation = () => {
  if (clueSkipAnimRef.current) {
    clearTimeout(clueSkipAnimRef.current);
    clueSkipAnimRef.current = null;
  }
  setClueSkipFlashKeys([]);
  setHeaderNextOverride(null);
};

const clearStatusMessage = () => {
  if (messageTimerRef.current) {
    clearTimeout(messageTimerRef.current);
    messageTimerRef.current = null;
  }
  setStatusMessage("");
};

const showMessage = (text) => {
  if (messageTimerRef.current) {
    clearTimeout(messageTimerRef.current);
    messageTimerRef.current = null;
  }
  setStatusMessage(text);
  messageTimerRef.current = setTimeout(() => {
    setStatusMessage("");
    messageTimerRef.current = null;
  }, 2500);
};

const trackMoveRejected = (reason, nextNumber) => {
  if (rejectedReasonsRef.current.has(reason)) return;
  rejectedReasonsRef.current.add(reason);
  if (window.gtag) {
    window.gtag("event", "move_rejected", {
      game_mode: gameMode,
      reason,
      next_number: nextNumber
    });
  }
};

useEffect(() => {
  return () => {
    if (clueSkipAnimRef.current) {
      clearTimeout(clueSkipAnimRef.current);
    }
    celebrationTimersRef.current.forEach(clearTimeout);
    if (winModalTimerRef.current) {
      clearTimeout(winModalTimerRef.current);
    }
    if (messageTimerRef.current) {
      clearTimeout(messageTimerRef.current);
      messageTimerRef.current = null;
    }
  };
}, []);

useEffect(() => {
  if (process.env.NODE_ENV !== 'development') return undefined;
  const applyCelebratePreview = () => {
    if (window.location.hash === '#celebrate') {
      setShowCelebration(true);
    }
  };
  applyCelebratePreview();
  window.addEventListener('hashchange', applyCelebratePreview);
  return () => window.removeEventListener('hashchange', applyCelebratePreview);
}, []);

useEffect(() => {
  if (!gridWrapperRef.current) return;

  const points = placementPath.length > 0 
    ? [...selectedCells, ...placementPath.slice(1)]
    : selectedCells;

  setOverlayPoints(
    points.map(([r, c], idx, arr) => ({
      row: r,
      col: c,
      placed: idx === arr.length - 1 && placementPath.length > 0
    }))
  );

}, [selectedCells, placementPath]);

useEffect(() => {
  if (gameMode) {
    clearClueSkipAnimation();
    clearStatusMessage();
    rejectedReasonsRef.current = new Set();
    const savedModeState = modeStateRef.current[gameMode];

    if (savedModeState) {
      setGrid(savedModeState.grid);
      setHistory(savedModeState.history);
      setGameWon(savedModeState.gameWon);
      setShowWinScreen(savedModeState.showWinScreen);
      setStartTime(savedModeState.startTime);
      setElapsedTime(savedModeState.elapsedTime);
      setMoveCount(savedModeState.moveCount);
      setLastSequence(savedModeState.lastSequence);
      setLastPlacedPosition(savedModeState.lastPlacedPosition);
      setWinScreenDismissed(savedModeState.winScreenDismissed);
    } else {
      setGrid(puzzle);
      setHistory([puzzle]);
      setGameWon(false);
      setShowWinScreen(false);
      setStartTime(null);
      setElapsedTime(0);
      setMoveCount(0);
      setLastSequence(null);
      setLastPlacedPosition(null);
      setWinScreenDismissed(false);
    }
    setSelectedCells([]);
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }
}, [gameMode, puzzle]);

useEffect(() => {
  if (!gameMode || !grid || !history) return;
  modeStateRef.current[gameMode] = {
    grid: grid.map((row) => [...row]),
    history: history.map((board) => board.map((row) => [...row])),
    gameWon,
    showWinScreen,
    startTime,
    elapsedTime,
    moveCount,
    lastSequence,
    lastPlacedPosition,
    winScreenDismissed
  };
}, [
  gameMode,
  grid,
  history,
  gameWon,
  showWinScreen,
  startTime,
  elapsedTime,
  moveCount,
  lastSequence,
  lastPlacedPosition,
  winScreenDismissed
]);

useEffect(() => {
  const prefilled = new Set();
  for (let r = 0; r < puzzle.length; r++) {
    for (let c = 0; c < puzzle[r].length; c++) {
      if (typeof puzzle[r][c] === "number") {
        prefilled.add(`${r},${c}`);
      }
    }
  }
  prefilledCells.current = prefilled;
}, [puzzle]);

useEffect(() => {
  if (showStartScreen) {
    setDailyStreakInfo(getDailyStreakInfo());
  }
}, [showStartScreen]);

useEffect(() => {
  const updateCountdown = () => {
    const now = new Date();
    const nowUTC = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
    const midnightEST = new Date(nowUTC);
    midnightEST.setHours(24, 0, 0, 0);
    const diffMs = midnightEST - nowUTC;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs / (1000 * 60)) % 60);
    const seconds = Math.floor((diffMs / 1000) % 60);
    setCountdownToMidnight(
      `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
    );
  };
  updateCountdown();
  const interval = setInterval(updateCountdown, 1000);
  return () => clearInterval(interval);
}, []);

useEffect(() => {
  const storedBestMini = localStorage.getItem("sums-best-time-mini");
  const storedBestFull = localStorage.getItem("sums-best-time-full");
  const storedHistory = localStorage.getItem("sums-game-history");
  
  if (storedBestMini) setBestTimeMini(Number(storedBestMini));
  if (storedBestFull) setBestTimeFull(Number(storedBestFull));
  if (storedHistory) setGameHistory(JSON.parse(storedHistory));
  
  const oldBest = localStorage.getItem("sums-best-time");
  if (oldBest && !storedBestFull) {
    setBestTimeFull(Number(oldBest));
    localStorage.setItem("sums-best-time-full", oldBest);
  }
}, []);

useEffect(() => {
  if (startTime && !gameWon && !showStartScreen && !timerRef.current) {
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setElapsedTime(elapsed);
    }, 1000);
  }
  return () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };
}, [startTime, gameWon, showStartScreen]);

useEffect(() => {
  const hasProgress = elapsedTime > 0 || history.length > 1;
  if (!showStartScreen && gameMode && !gameWon && !startTime && hasProgress) {
    setStartTime(Date.now() - (elapsedTime * 1000));
  }
}, [showStartScreen, gameMode, gameWon, startTime, elapsedTime, history]);

useEffect(() => {
  if (!startTime || gameWon || showStartScreen) return;
  const syncElapsed = () => {
    setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
  };

  window.addEventListener("focus", syncElapsed);
  document.addEventListener("visibilitychange", syncElapsed);
  return () => {
    window.removeEventListener("focus", syncElapsed);
    document.removeEventListener("visibilitychange", syncElapsed);
  };
}, [startTime, gameWon, showStartScreen]);

useEffect(() => {
  if (showWinScreen && window.adsbygoogle && !window.adsbygoogle.initialized) {
    try {
      window.adsbygoogle.push({});
      window.adsbygoogle.initialized = true;
    } catch (e) {
      console.error("Adsbygoogle push error:", e);
    }
  }
}, [showWinScreen]);

useEffect(() => {
  const handleBeforeUnload = () => {
    if (startTime && !gameWon && window.gtag) {
      window.gtag('event', 'game_abandon', {
        game_mode: gameMode,
        time_played_seconds: elapsedTime,
        total_moves: moveCount
      });
    }
  };
  
  window.addEventListener('beforeunload', handleBeforeUnload);
  return () => window.removeEventListener('beforeunload', handleBeforeUnload);
}, [startTime, gameWon, gameMode, elapsedTime, moveCount]);

useEffect(() => {
  const hasCooldown = hintCooldownByMode.mini > 0 || hintCooldownByMode.full > 0 || hintCooldownByMode.warmup > 0 || hintCooldownByMode.warmupFull > 0;
  if (!hasCooldown) return;
  const interval = setInterval(() => {
    setHintCooldownByMode((prev) => ({
      mini: Math.max(0, prev.mini - 100),
      full: Math.max(0, prev.full - 100),
      warmup: Math.max(0, prev.warmup - 100),
      warmupFull: Math.max(0, prev.warmupFull - 100)
    }));
  }, 100);
  return () => clearInterval(interval);
}, [hintCooldownByMode]);

const tutorialTriggerRef = useRef(null);
const tutorialStepRef = useRef(0);
const tutorialCompletedThisOpenRef = useRef(false);
const pendingPracticeRef = useRef(null);

const openTutorial = useCallback((trigger, ending = 'daily') => {
  if (ending !== 'practice') pendingPracticeRef.current = null;
  tutorialTriggerRef.current = trigger;
  tutorialStepRef.current = 0;
  tutorialCompletedThisOpenRef.current = false;
  setTutorialEnding(ending);
  setShowInstructions(true);
  if (window.gtag) window.gtag('event', 'tutorial_view', { trigger });
}, []);

const dismissTutorial = useCallback(() => {
  markTutorialSeen();
  setShowInstructions(false);
}, []);

const closeInstructions = useCallback(() => {
  if (!tutorialCompletedThisOpenRef.current && window.gtag) {
    window.gtag('event', 'tutorial_close', {
      trigger: tutorialTriggerRef.current,
      step: tutorialStepRef.current
    });
  }
  pendingPracticeRef.current = null;
  setShowInstructions(false);
}, []);

const openBrowsingTutorial = (trigger) => {
  openTutorial(trigger, readWarmupAvailable() ? 'back' : 'daily');
};

const handleTutorialStepChange = useCallback((step) => {
  tutorialStepRef.current = step;
  if (step === 6 && !tutorialCompletedThisOpenRef.current) {
    tutorialCompletedThisOpenRef.current = true;
    if (window.gtag) {
      window.gtag('event', 'tutorial_complete', {
        trigger: tutorialTriggerRef.current
      });
    }
  }
}, []);

const currentHintCooldownRemaining = gameMode ? hintCooldownByMode[gameMode] : 0;

const startGame = (mode) => {
  const isResumingSameMode = gameMode === mode && !!grid;
  setGameMode(mode);
  setShowStartScreen(false);
  if (isResumingSameMode) {
    if (gameWon) setShowWinScreen(true);
  } else if (window.gtag) {
    window.gtag('event', 'game_start', { game_mode: mode });
  }
};

const startGameFromTutorial = (mode) => {
  startGame(mode);
  dismissTutorial();
};

const shouldStartWarmup = () => {
  if (warmupFinishedRef.current) return false;
  return readWarmupAvailable();
};

const startWarmup = () => {
  if (showInstructions) dismissTutorial();
  startGame('warmup');
};

const startWarmupFull = () => {
  if (showInstructions) dismissTutorial();
  startGame('warmupFull');
};

const startPracticeFromTutorial = () => {
  const mode = pendingPracticeRef.current === 'full' ? 'warmupFull' : 'warmup';
  pendingPracticeRef.current = null;
  if (showInstructions) dismissTutorial();
  else markTutorialSeen();
  startGame(mode);
};

const startMiniFromEntry = () => {
  if (shouldStartWarmup()) {
    if (!hasSeenTutorial()) {
      pendingPracticeRef.current = 'mini';
      openTutorial('play_mini', 'practice');
      return;
    }
    startWarmup();
    return;
  }
  if (showInstructions) {
    startGameFromTutorial('mini');
    return;
  }
  startGame('mini');
};

const startFullFromEntry = () => {
  if (shouldStartWarmup()) {
    if (!hasSeenTutorial()) {
      pendingPracticeRef.current = 'full';
      openTutorial('play_full', 'practice');
      return;
    }
    startWarmupFull();
    return;
  }
  if (showInstructions) {
    startGameFromTutorial('full');
    return;
  }
  startGame('full');
};

const getDropTargetHighlight = (r, c) => {
  if (!isValidCell(r, c, grid)) return false;
  if (grid[r][c] !== null) return false;
  if (!selectedCells.length) return false;

  const sum = selectedCells.reduce((acc, [sr, sc]) => acc + grid[sr][sc], 0);
  const expected = getNextExpectedNumber(grid, puzzle);

  const isAdjacentToLastSelected = (() => {
    const [lr, lc] = selectedCells[selectedCells.length - 1];
    return getAdjacent([lr, lc], grid).some(([ar, ac]) => ar === r && ac === c);
  })();

  return (
    isConnectedGroup(selectedCells, grid) &&
    sum === expected &&
    isAdjacentToLastSelected
  );
};

const clearCelebrationTimers = () => {
  celebrationTimersRef.current.forEach(clearTimeout);
  celebrationTimersRef.current = [];
  if (winModalTimerRef.current) {
    clearTimeout(winModalTimerRef.current);
    winModalTimerRef.current = null;
  }
};

const stopWinCelebration = () => {
  clearCelebrationTimers();
  setShowCelebration(false);
  setCelebratingCells([]);
};

const triggerWinCellCelebration = (origin, board = grid) => {
  const cells = [];
  board.forEach((row, rIdx) => {
    row.forEach((cell, cIdx) => {
      if (cell !== undefined && cell !== "X") {
        cells.push([rIdx, cIdx]);
      }
    });
  });
  const [originR, originC] = origin || [
    Math.floor(board.length / 2),
    Math.floor((board[0]?.length || 1) / 2)
  ];
  cells.sort((a, b) => {
    const da = (a[0] - originR) ** 2 + (a[1] - originC) ** 2;
    const db = (b[0] - originR) ** 2 + (b[1] - originC) ** 2;
    return da - db;
  });
  cells.forEach(([r, c], idx) => {
    const id = setTimeout(() => {
      setCelebratingCells(prev => [...prev, `${r},${c}`]);
    }, idx * 70);
    celebrationTimersRef.current.push(id);
  });
};

const shareWinMessage = () => {
  const sequence = lastSequence?.join(" + ") || "?";
  const sum = lastSequence?.reduce((a, b) => a + b, 0) || "?";
  const timeStr = formatTime(elapsedTime);
  const gameTitle = gameMode === 'mini' ? 'Mini SUMS' : 'SUMS';

  const message = `${gameTitle}: ${sequence} = ${sum} | ${timeStr}\nPlay at https://sums.games`;

  if (navigator.share) {
    navigator.share({
      title: `${gameTitle} on SUMS`,
      text: message,
      url: 'https://sums.games/',
    }).catch(err => console.error('Error sharing:', err));
  } else {
    alert(`Copy and share:\n${message}`);
  }
};

const isNextToLastSelected = (r, c) => {
  if (!selectedCells.length) return false;
  const [lr, lc] = selectedCells[selectedCells.length - 1];
  return getAdjacent([lr, lc], grid).some(([ar, ac]) => ar === r && ac === c);
};

const applyPlacementMove = (selectionPath, r, c) => {
  const sum = selectionPath.reduce((acc, [sr, sc]) => acc + grid[sr][sc], 0);
  const expected = getNextExpectedNumber(grid, puzzle);
  const canPlace = isConnectedGroup(selectionPath, grid) &&
    sum === expected &&
    selectionPath.length > 0 &&
    getAdjacent(selectionPath[selectionPath.length - 1], grid).some(([ar, ac]) => ar === r && ac === c);

  if (!canPlace) return false;

  const nextAfter = expected + 1;
  const clueIsBlocked = puzzle.flat().includes(nextAfter) && isNextNumberBlockedByClue(grid, puzzle);

  if (clueIsBlocked) {
    setAlertTitle("Stuck");
    setAlertMessage(`The gray ${nextAfter} can't be reached from here. Undo a few moves and try another route.`);
    setShowAlertModal(true);
    return false;
  }

  const placedKey = `${r},${c}`;
  const newGrid = grid.map(row => [...row]);
  newGrid[r][c] = sum;

  const sequence = selectionPath.map(([sr, sc]) => grid[sr][sc]);
  setLastSequence(sequence);

  const nextNum = expected + 1;
  const nextIsClue = puzzle.flat().includes(nextNum);
  if (nextIsClue) {
    const tempGrid = newGrid;
    const nextClueCells = [];
    for (let rr = 0; rr < tempGrid.length; rr++) {
      for (let cc = 0; cc < tempGrid[rr].length; cc++) {
        if (tempGrid[rr][cc] === nextNum && puzzle?.[rr]?.[cc] === nextNum) {
          nextClueCells.push([rr, cc]);
        }
      }
    }

    if (nextClueCells.length > 0) {
      const candidates = [];
      for (let rr = 0; rr < tempGrid.length; rr++) {
        for (let cc = 0; cc < tempGrid[rr].length; cc++) {
          const val = tempGrid[rr][cc];
          if (typeof val === "number" && val !== nextNum) {
            candidates.push([rr, cc]);
          }
        }
      }

      const groups = findConnectedGroupsThatSum(tempGrid, candidates, nextNum);
      let hasValidGroup = false;

      for (const [cr, cc] of nextClueCells) {
        for (const group of groups) {
          const gsum = group.reduce((acc, [gr, gc]) => acc + tempGrid[gr][gc], 0);
          const connected = isConnectedGroup(group, tempGrid);
          const touchesClue = getAdjacent([cr, cc], tempGrid).some(([ar, ac]) =>
            group.some(([gr, gc]) => gr === ar && gc === ac)
          );

          if (gsum === nextNum && connected && touchesClue) {
            hasValidGroup = true;
            break;
          }
        }
        if (hasValidGroup) break;
      }

      if (!hasValidGroup) {
        setAlertTitle("Not there");
        setAlertMessage(`If ${expected} goes there, no chain touching the gray ${nextNum} can add up to ${nextNum}. Try another square, or undo a few moves.`);
        trackMoveRejected("gray_unreachable", expected);
        setShowAlertModal(true);
        setSelectedCells([]);
        setHintInProgress(false);
        setPlacementPath([]);
        setOverlayPoints([]);
        return false;
      }
    }
  }

  setGrid(newGrid);
  setHistory(prev => [...prev, newGrid]);
  setLastPlacedPosition([r, c]);
  setMoveCount(prev => prev + 1);

  const lastSelected = selectionPath[selectionPath.length - 1];
  setPlacementPath([lastSelected, [r, c]]);
  setPoppingCells((prev) => [...prev, placedKey]);

  setTimeout(() => {
    setPoppingCells((prev) => prev.filter((k) => k !== placedKey));
    setPlacementPath([]);
    setSelectedCells([]);
    setOverlayPoints([]);
    setHintInProgress(false);
  }, 10);

  const solverNext = getNextExpectedNumber(newGrid, puzzle);
  const skipChain = getPrefilledCluesSkippedBeforeNext(newGrid, puzzle, expected, solverNext);
  clearClueSkipAnimation();
  const PRE_CLUE_FLASH_PAUSE_MS = 500;
  const CLUE_SKIP_STEP_MS = 500;
  if (skipChain.length > 0) {
    let step = 0;
    const runStep = () => {
      if (step >= skipChain.length) {
        setClueSkipFlashKeys([]);
        setHeaderNextOverride(null);
        clueSkipAnimRef.current = null;
        return;
      }
      const seg = skipChain[step];
      setHeaderNextOverride(seg.value);
      setClueSkipFlashKeys(seg.positions.map(([sr, sc]) => `${sr},${sc}`));
      step++;
      clueSkipAnimRef.current = setTimeout(runStep, CLUE_SKIP_STEP_MS);
    };
    clueSkipAnimRef.current = setTimeout(runStep, PRE_CLUE_FLASH_PAUSE_MS);
  }

  const allFilled = newGrid.every(row => row.every(cell => cell === "X" || cell !== null));
  if (allFilled) {
    clearInterval(timerRef.current);
    setGameWon(true);
    setShowCelebration(true);
    triggerWinCellCelebration([r, c], newGrid);
    const delay = prefersReducedMotion() ? WIN_CELEBRATION_REDUCED_MS : WIN_CELEBRATION_MS;

    const finalTime = Math.floor((Date.now() - startTime) / 1000);
    const finalMoves = moveCount + 1;

    if (isWarmupMode(gameMode)) {
      warmupFinishedRef.current = true;
      try {
        localStorage.setItem("sums-warmup-done", "1");
      } catch (e) {
        // Storage can be blocked; this session still treats the warm-up as finished.
      }
    } else {
      const streakResult = recordDailyPuzzleWin(gameMode);
      setLastWinStreakCount(streakResult.count);
      setDailyStreakInfo(getDailyStreakInfo());
    }

    winModalTimerRef.current = setTimeout(() => {
      if (isWarmupMode(gameMode)) {
        setShowWarmupDoneScreen(true);
      } else {
        setShowWinScreen(true);
      }

      if (window.gtag) {
        const streaks = getDailyStreakInfo();
        window.gtag('event', 'game_complete', {
          game_mode: gameMode,
          completion_time_seconds: finalTime,
          total_moves: finalMoves,
          daily_streak_mini: streaks.mini.count,
          daily_streak_full: streaks.full.count
        });
      }
    }, delay);

    if (gameMode === 'mini') {
      if (!bestTimeMini || finalTime < bestTimeMini) {
        setBestTimeMini(finalTime);
        localStorage.setItem("sums-best-time-mini", finalTime.toString());
      }
    } else if (gameMode === 'full') {
      if (!bestTimeFull || finalTime < bestTimeFull) {
        setBestTimeFull(finalTime);
        localStorage.setItem("sums-best-time-full", finalTime.toString());
      }
    }

    if (!isWarmupMode(gameMode)) {
      const updatedHistory = [...gameHistory, { time: finalTime, mode: gameMode, date: new Date().toISOString() }];
      setGameHistory(updatedHistory);
      localStorage.setItem("sums-game-history", JSON.stringify(updatedHistory));
    }
  }

  return true;
};

const handleHint = () => {
  if (hintInProgress || gameWon || currentHintCooldownRemaining > 0) return;
  if (!startTime) {
    setStartTime(Date.now() - (elapsedTime * 1000));
  }

  setSelectedCells([]);
  setHintInProgress(true);
  if (gameMode) {
    setHintCooldownByMode((prev) => ({ ...prev, [gameMode]: HINT_COOLDOWN_MS }));
  }
  const HINT_THINK_DELAY_MS = 80;
  const HINT_SELECTION_STEP_MS = 140;
  const HINT_SELECTION_HOLD_MS = 320;

  setTimeout(() => {
    const hintMove = getHintMove(grid, puzzle, hardMode);
    if (!hintMove) {
      setHintInProgress(false);
      setAlertTitle("No hint");
      setAlertMessage("Hint couldn't find a move from here. Undo a few moves and try again.");
      setShowAlertModal(true);
      return;
    }

    setSelectedCells([]);
    hintMove.selectedPath.forEach((_, idx) => {
      setTimeout(() => {
        setSelectedCells(hintMove.selectedPath.slice(0, idx + 1));
      }, idx * HINT_SELECTION_STEP_MS);
    });

    const totalSelectionMs = hintMove.selectedPath.length * HINT_SELECTION_STEP_MS;
    setTimeout(() => {
      const [pr, pc] = hintMove.placeCell;
      const placed = applyPlacementMove(hintMove.selectedPath, pr, pc);
      if (!placed) {
        setHintInProgress(false);
      }
    }, totalSelectionMs + HINT_SELECTION_HOLD_MS);
  }, HINT_THINK_DELAY_MS);
};

const handleCellClick = (r, c, event) => {
  if (gameWon || showCelebration) return;
  if (!grid || !grid.length || !grid[0]) {
    console.error('Grid not loaded yet');
    return;
  }
  
  if (event) event.preventDefault();

  const next = getNextExpectedNumber(grid, puzzle);
  const clueBlocked = puzzle.flat().includes(next) && isNextNumberBlockedByClue(grid, puzzle);
  if (clueBlocked) return;

  if (grid[r][c] === null && puzzle?.[r]?.[c] !== null && puzzle?.[r]?.[c] !== undefined) {
    return;
  }

  if (!startTime) {
    console.log('Setting startTime:', Date.now());
    setStartTime(Date.now() - (elapsedTime * 1000));
  }

  const cellValue = grid[r][c];
  if (cellValue === "X") return;

  if (cellValue !== null) {
    const selectedIndex = selectedCells.findIndex(([sr, sc]) => sr === r && sc === c);

    if (selectedIndex !== -1) {
      if (selectedIndex === 0 && selectedCells.length === 1) {
        setSelectedCells([]);
      } else {
        setSelectedCells(selectedCells.slice(0, selectedIndex));
      }
      return;
    }

    if (selectedCells.length === 0) {
      setSelectedCells([[r, c]]);
      return;
    }

    const [lastR, lastC] = selectedCells[selectedCells.length - 1];
    const isAdjacent = getAdjacent([lastR, lastC], grid).some(
      ([ar, ac]) => ar === r && ac === c
    );

    if (isAdjacent) {
      if (hardMode && selectedCells.length >= getMaxSelection(hardMode, puzzle)) {
        const maxSelection = getMaxSelection(hardMode, puzzle);
        showMessage(`Hard mode: chains can be at most ${maxSelection} numbers.`);
        trackMoveRejected("hard_mode_cap", next);
        setSelectedCells([]);
        return;
      }
      setSelectedCells([...selectedCells, [r, c]]);
    } else {
      showMessage("That number doesn't touch your last one. Diagonals count.");
      trackMoveRejected("chain_gap", next);
    }

  } else {
    const sum = selectedCells.reduce((acc, [sr, sc]) => acc + grid[sr][sc], 0);
    const expected = getNextExpectedNumber(grid, puzzle);
    const canPlace = isConnectedGroup(selectedCells, grid) &&
      sum === expected &&
      isNextToLastSelected(r, c);

    if (selectedCells.length === 0) {
      showMessage(`Tap numbers that add up to ${expected} first, then an empty square.`);
      trackMoveRejected("empty_first", expected);
      return;
    }

    if (canPlace) {
      applyPlacementMove(selectedCells, r, c);
      return;
    }

    if (sum !== expected) {
      setSelectedCells([]);
      showMessage(`That adds up to ${sum}. You need ${expected}.`);
      trackMoveRejected("wrong_sum", expected);
      return;
    }

    if (!isNextToLastSelected(r, c)) {
      showMessage(`Put ${expected} in a square touching the last number you tapped.`);
      trackMoveRejected("not_touching_last", expected);
      return;
    }

    setSelectedCells([]);
  }
};

const handleUndo = () => {
  clearClueSkipAnimation();
  clearStatusMessage();
  if (history.length > 1) {
    const newHistory = [...history];
    newHistory.pop();
    setGrid(newHistory[newHistory.length - 1]);
    setHistory(newHistory);
    setSelectedCells([]);
    setGameWon(false);
    stopWinCelebration();
    setShowWinScreen(false);
    setShowWarmupDoneScreen(false);
    setWinScreenDismissed(false);
  }
};

const handleClear = () => {
  clearStatusMessage();
  setShowClearConfirm(true);
};

const confirmClear = () => {
  clearClueSkipAnimation();
  clearStatusMessage();
  rejectedReasonsRef.current = new Set();
  const clearedGrid = puzzle.map(row => [...row]);
  setGrid(clearedGrid);
  setHistory([clearedGrid]);
  setSelectedCells([]);
  setGameWon(false);
  stopWinCelebration();
  setShowWinScreen(false);
  setShowWarmupDoneScreen(false);
  setWinScreenDismissed(false);
  setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
  setShowClearConfirm(false);
};

const handleResetStats = () => {
  setBestTimeMini(null);
  setBestTimeFull(null);
  setGameHistory([]);
  localStorage.removeItem('sums-best-time-mini');
  localStorage.removeItem('sums-best-time-full');
  localStorage.removeItem('sums-game-history');
  localStorage.removeItem('sums-best-time');
  clearDailyStreak();
  setDailyStreakInfo(getDailyStreakInfo());
};

if (showStartScreen) {
  return (
    <>
      <StartScreen
        onPlayMini={startMiniFromEntry}
        onPlayFull={startFullFromEntry}
        onShowInstructions={() => openBrowsingTutorial('start_screen')}
        onShowStats={() => setShowStats(true)}
        onShowHighScores={() => {
          setHighScoresHighlight(null);
          setShowHighScores(true);
        }}
        todayStr={todayStr}
        puzzleNumber={puzzleNumber}
        puzzleNumberMini={puzzleNumberMini}
        streakMini={dailyStreakInfo.mini}
        streakFull={dailyStreakInfo.full}
      />

      {showInstructions && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          overflowY: "auto",
          backgroundColor: "rgba(0,0,0,0.6)",
          zIndex: 1000,
          padding: "40px 16px",
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          touchAction: "manipulation"
        }}>
          <div style={{
            background: "#fff",
            padding: window.innerWidth < 480 ? 16 : 20,
            borderRadius: 8,
            width: "100%",
            maxWidth: "420px",
            boxSizing: "border-box",
            textAlign: "left",
            position: "relative"
          }}>
            <button
              onClick={closeInstructions}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                background: "transparent",
                border: "none",
                fontSize: "1.4rem",
                cursor: "pointer",
                lineHeight: "1",
                padding: 0
              }}
              aria-label="Close instructions"
            >✖</button>

            <InteractiveTutorial 
              onComplete={() => {
                dismissTutorial();
                setShowStartScreen(false);
              }}
              onPlayMini={startMiniFromEntry}
              onPlayFull={startFullFromEntry}
              onPlayPractice={startPracticeFromTutorial}
              onBack={closeInstructions}
              onStepChange={handleTutorialStepChange}
              ending={tutorialEnding}
            />

          </div>
        </div>
      )}

      {showPrivacyModal && (
        <PrivacyPolicyModal modal onClose={() => setShowPrivacyModal(false)} />
      )}

      {showAboutModal && (
        <AboutModal modal onClose={() => setShowAboutModal(false)} />
      )}

      {showStrategyModal && (
        <StrategyModal modal onClose={() => setShowStrategyModal(false)} />
      )}

      {showStats && (
        <Statistics 
          onClose={() => setShowStats(false)}
          bestTimeMini={bestTimeMini}
          bestTimeFull={bestTimeFull}
          gameHistory={gameHistory}
          dailyStreakMini={dailyStreakInfo.mini.count}
          dailyStreakFull={dailyStreakInfo.full.count}
          onResetStats={handleResetStats}
        />
      )}

      {showHighScores && (
        <HighScores
          onClose={() => setShowHighScores(false)}
          highlightEntry={highScoresHighlight}
        />
      )}

      <FireworksCelebration
        active={showCelebration}
        showBanner={!showWinScreen}
        linger={showWinScreen}
      />
    </>
  );
}

if (!gameMode) return null;
if (!grid || !Array.isArray(grid) || grid.length === 0) return null;
if (!puzzle || puzzle.length !== grid.length || puzzle[0]?.length !== grid[0]?.length) {
  console.warn('Puzzle and grid size mismatch, waiting for sync...');
  return null;
}

const solverNextExpected = getNextExpectedNumber(grid, puzzle);
const isPuzzleComplete = grid.every(row => row.every(cell => cell === "X" || cell !== null));
const lastSum = puzzle.flat().filter(cell => cell !== "X" && cell !== undefined).length;

return (
  <>
    <div className="game-wrapper"
      style={{
        position: "relative",
        textAlign: "center",
        padding: "25px",
        maxWidth: "600px",
        margin: "auto"
      }}
    >
      <GameHeader
        elapsedTime={elapsedTime}
        nextExpectedNumber={isPuzzleComplete ? null : (headerNextOverride ?? solverNextExpected)}
        currentSum={selectedCells.length > 0
          ? selectedCells.reduce((acc, [r, c]) => acc + grid[r][c], 0)
          : 0}
        lastSum={lastSum}
        showHelpDropdown={showHelpDropdown}
        setShowHelpDropdown={setShowHelpDropdown}
        onOpenInstructions={() => openBrowsingTutorial('in_game')}
        hardMode={hardMode}
        setHardMode={setHardMode}
        setSelectedCells={setSelectedCells}
        onReturnHome={() => {
          if (startTime && !gameWon && window.gtag) {
            window.gtag('event', 'game_abandon', {
              game_mode: gameMode,
              time_played_seconds: elapsedTime,
              total_moves: moveCount
            });
          }
          if (startTime && !gameWon) {
            setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
            setStartTime(null);
          }
          stopWinCelebration();
          setShowWarmupDoneScreen(false);
          setShowStartScreen(true);
        }}
        gameMode={gameMode}
        puzzleNumber={currentPuzzleNumber}
      />

      <div
        aria-live="polite"
        style={{
          height: "22px",
          lineHeight: "22px",
          fontSize: "14px",
          color: "#555",
          textAlign: "center"
        }}
      >
        {statusMessage}
      </div>

      <div ref={gridWrapperRef} style={{ 
        position: "relative", 
        display: "inline-block", 
        zIndex: 1
      }}>
        <CanvasOverlay
          overlayPoints={overlayPoints}
          grid={grid}
          cellSize={cellSize}
          margin={2}
          clueFlashCells={clueFlashCells}
        />
        <div className={`grid${celebratingCells.length ? ' sums-grid-celebrating' : ''}`} style={{ 
          position: "relative", 
          zIndex: 2,
          overflow: "visible"
        }}>
          {grid && grid.length > 0 && grid.map((row, rIdx) => (
            <div key={rIdx} style={{ display: "flex" }}>
              {row.map((cell, cIdx) => {
                const key = `${rIdx},${cIdx}`;
                const isPopping = poppingCells.includes(key);
                const isCelebrating = celebratingCells.includes(key); 
                const isDropTarget = getDropTargetHighlight(rIdx, cIdx);
                const isPrefilledClue = puzzle?.[rIdx]?.[cIdx] === cell && typeof cell === "number";
                const cellStyle = {
                  width: cellSize,
                  height: cellSize,
                  margin: 2,
                  lineHeight: `${cellSize}px`,
                  fontSize: Math.floor(cellSize / 2.5),
                  textAlign: "center",
                  borderRadius: 0,
                  backgroundColor:
                    cell === "X" ? "#303036" :
                    isPopping ? "#d4f8d4" :
                    isDropTarget ? "#b3eaf2" :
                    isPrefilledClue ? "#e3e6ec" :
                    "#fff",
                  color: "#303036",
                  fontWeight: isPrefilledClue ? "bold" : "normal",
                  border: cell === "X" ? "1px solid #303036" : "1px solid #999",
                  transition: "transform 0.2s ease, background-color 0.3s ease",
                  ...(isCelebrating ? {} : { transform: "scale(1)" }),
                  cursor: "pointer",
                  position: "relative",
                  zIndex: 1,
                  WebkitTapHighlightColor: "transparent",
                  userSelect: "none",
                  touchAction: "manipulation"
                };

                return (
                  <div 
                    key={key}
                    className={`cell-${rIdx}-${cIdx}${isCelebrating ? ' sums-cell-ignite' : ''}`}
                    style={cellStyle}
                    onClick={(e) => handleCellClick(rIdx, cIdx, e)}
                    onTouchEnd={(e) => handleCellClick(rIdx, cIdx, e)}
                  />
                );
              })}
            </div>
          ))}
        </div>

        <NumberOverlay
          grid={grid}
          puzzle={puzzle}
          cellSize={cellSize}
          margin={2}
        />
      </div>

      <GameControls
        hardMode={hardMode}
        puzzle={puzzle}
        onUndo={handleUndo}
        onClear={handleClear}
        onHint={handleHint}
        hintInProgress={hintInProgress}
        hintCooldownRemaining={currentHintCooldownRemaining}
        hintDisabled={gameWon || showCelebration}
        canUndo={history.length > 1}
        gameWon={gameWon || showCelebration}
      />

      <FireworksCelebration
        active={showCelebration}
        showBanner={!showWinScreen && !showWarmupDoneScreen}
        linger={showWinScreen || showWarmupDoneScreen}
      />
      {showCelebration && (
        <div role="status" aria-live="assertive" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
          You won!
        </div>
      )}

      {showWinScreen && (
        <WinScreen
          elapsedTime={elapsedTime}
          countdownToMidnight={countdownToMidnight}
          onClose={() => {
            setShowWinScreen(false);
            setWinScreenDismissed(true);
            stopWinCelebration();
          }}
          onShare={shareWinMessage}
          gameMode={gameMode}
          streakCount={lastWinStreakCount}
          onViewHighScores={(highlight) => {
            stopWinCelebration();
            setHighScoresHighlight(highlight);
            setShowHighScores(true);
          }}
          onReturnHome={() => {
            setShowWinScreen(false);
            setWinScreenDismissed(true);
            stopWinCelebration();
            setShowWarmupDoneScreen(false);
            setShowStartScreen(true);
          }}
        />
      )}

      {showWarmupDoneScreen && (
        <WarmupDoneScreen
          offer={gameMode === 'warmupFull' ? 'mini' : 'full'}
          onPlayNext={() => {
            const nextMode = gameMode === 'warmupFull' ? 'mini' : 'full';
            setShowWarmupDoneScreen(false);
            stopWinCelebration();
            startGame(nextMode);
          }}
          onReturnHome={() => {
            setShowWarmupDoneScreen(false);
            stopWinCelebration();
            setShowStartScreen(true);
          }}
        />
      )}

      {showInstructions && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          overflowY: "auto",
          backgroundColor: "rgba(0,0,0,0.6)",
          zIndex: 1000,
          padding: "40px 16px",
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          touchAction: "manipulation"
        }}>
          <div style={{
            background: "#fff",
            padding: window.innerWidth < 480 ? 16 : 20,
            borderRadius: 8,
            width: "100%",
            maxWidth: "420px",
            boxSizing: "border-box",
            textAlign: "left",
            position: "relative"
          }}>
            <button
              onClick={closeInstructions}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                background: "transparent",
                border: "none",
                fontSize: "1.4rem",
                cursor: "pointer",
                lineHeight: "1",
                padding: 0
              }}
              aria-label="Close instructions"
            >✖</button>

            <InteractiveTutorial 
              onComplete={() => dismissTutorial()}
              onPlayMini={startMiniFromEntry}
              onPlayFull={startFullFromEntry}
              onPlayPractice={startPracticeFromTutorial}
              onBack={closeInstructions}
              onStepChange={handleTutorialStepChange}
              ending={tutorialEnding}
            />

          </div>
        </div>
      )}

      <footer style={{ textAlign: 'center', marginTop: '30px', fontSize: '0.9rem' }}>
        <span
          onClick={(e) => {
            e.stopPropagation();
            setShowPrivacyModal(true);
          }}
          style={{ margin: '0 10px', color: '#666', textDecoration: 'none', cursor: 'pointer' }}
        >
          Privacy Policy
        </span>
        <span
          onClick={(e) => {
            e.stopPropagation();
            setShowAboutModal(true);
          }}
          style={{ margin: '0 10px', color: '#666', textDecoration: 'none', cursor: 'pointer' }}
        >
          About
        </span>
        <span
          onClick={(e) => {
            e.stopPropagation();
            setShowStrategyModal(true);
          }}
          style={{ margin: '0 10px', color: '#666', textDecoration: 'none', cursor: 'pointer' }}
        >
          Strategy
        </span>
        <p style={{ marginTop: '10px', fontSize: "clamp(14px, 2.5vw, 14px)", color: "#666" }}>
          © 2026 Xavier Games
        </p>
      </footer>
    </div>

    {showPrivacyModal && (
      <PrivacyPolicyModal modal onClose={() => setShowPrivacyModal(false)} />
    )}

    {showAboutModal && (
      <AboutModal modal onClose={() => setShowAboutModal(false)} />
    )}

    {showStrategyModal && (
      <StrategyModal modal onClose={() => setShowStrategyModal(false)} />
    )}

    {showHighScores && (
      <HighScores
        onClose={() => setShowHighScores(false)}
        highlightEntry={highScoresHighlight}
      />
    )}

    {showClearConfirm && (
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 3000
      }}>
        <div style={{
          background: "#fff",
          padding: "30px",
          borderRadius: "12px",
          boxShadow: "0 0 20px rgba(0,0,0,0.3)",
          textAlign: "center",
          maxWidth: "400px",
          width: "90%"
        }}>
          <h3 style={{ marginBottom: "20px", fontSize: "1.3rem", color: "#303036" }}>
            Clear Puzzle?
          </h3>
          <p style={{ marginBottom: "25px", fontSize: "1rem", lineHeight: "1.5", color: "#555" }}>
            You are about to clear the puzzle of all selections and restart. Are you sure you want to continue?
          </p>
          
          <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
            <button
              onClick={() => setShowClearConfirm(false)}
              style={{
                padding: "10px 24px",
                fontSize: "1rem",
                borderRadius: "24px",
                border: "2px solid #303036",
                backgroundColor: "#fff",
                color: "#303036",
                cursor: "pointer",
                fontWeight: "600",
                minWidth: "100px"
              }}
            >
              No
            </button>
            <button
              onClick={confirmClear}
              style={{
                padding: "10px 24px",
                fontSize: "1rem",
                borderRadius: "24px",
                border: "none",
                backgroundColor: "#e53935",
                color: "#fff",
                cursor: "pointer",
                fontWeight: "600",
                minWidth: "100px"
              }}
            >
              Yes, Clear
            </button>
          </div>
        </div>
      </div>
    )}

    {showAlertModal && (
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 3000
      }}>
        <div style={{
          background: "#fff",
          padding: "30px",
          borderRadius: "12px",
          boxShadow: "0 0 20px rgba(0,0,0,0.3)",
          textAlign: "center",
          maxWidth: "400px",
          width: "90%"
        }}>
          <h3 style={{ marginBottom: "20px", fontSize: "1.3rem", color: "#303036" }}>
            {alertTitle}
          </h3>
          <p style={{ marginBottom: "25px", fontSize: "1rem", lineHeight: "1.5", color: "#555" }}>
            {alertMessage}
          </p>
          
          <button
            onClick={() => setShowAlertModal(false)}
            style={{
              padding: "10px 24px",
              fontSize: "1rem",
              borderRadius: "24px",
              border: "none",
              backgroundColor: "#303036",
              color: "#fff",
              cursor: "pointer",
              fontWeight: "600",
              minWidth: "100px"
            }}
          >
            OK
          </button>
        </div>
      </div>
    )}
  </>
);
}